/**
 * Dataset type definitions for the creation wizard
 */

export type CompressionCodec =
  | 'uncompressed'
  | 'snappy'
  | 'gzip'
  | 'lzo'
  | 'brotli'
  | 'lz4'
  | 'zstd';

export type DatasetFormat =
  | 'parquet'
  | 'csv'
  | 'json'
  | 'avro'
  | 'orc';

export type DatasetType =
  | 'table'
  | 'view'
  | 'external';

export interface PartitionField {
  id: string;
  name: string;
}

export interface MetadataField {
  id: string;
  key: string;
  value: string;
}

export interface CreateDatasetRequest {
  name: string;
  qualified_name: string;
  type: DatasetType;
  location: string;
  format: DatasetFormat;
  compression: CompressionCodec;
  schema_id: string | null;
  partitions: string[];
  tags: string[];
  metadata: Record<string, any>;
  description?: string;
}

export interface DatasetValidationError {
  field?: string;
  message: string;
  code?: string;
}

/**
 * Compression codec display names
 */
export const COMPRESSION_CODECS: Array<{
  value: CompressionCodec;
  label: string;
  description: string;
}> = [
  {
    value: 'uncompressed',
    label: 'Uncompressed',
    description: 'No compression - fastest read/write, largest size',
  },
  {
    value: 'snappy',
    label: 'Snappy',
    description: 'Good compression with fast performance',
  },
  {
    value: 'gzip',
    label: 'GZIP',
    description: 'High compression ratio, slower performance',
  },
  {
    value: 'lzo',
    label: 'LZO',
    description: 'Fast compression, lower compression ratio',
  },
  {
    value: 'brotli',
    label: 'Brotli',
    description: 'High compression ratio, good for web transfers',
  },
  {
    value: 'lz4',
    label: 'LZ4',
    description: 'Very fast compression/decompression',
  },
  {
    value: 'zstd',
    label: 'ZSTD',
    description: 'Excellent compression ratio with good speed',
  },
];

/**
 * Dataset formats
 */
export const DATASET_FORMATS: Array<{
  value: DatasetFormat;
  label: string;
}> = [
  { value: 'parquet', label: 'Parquet' },
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
  { value: 'avro', label: 'Avro' },
  { value: 'orc', label: 'ORC' },
];

/**
 * Dataset types
 */
export const DATASET_TYPES: Array<{
  value: DatasetType;
  label: string;
  description: string;
}> = [
  {
    value: 'table',
    label: 'Table',
    description: 'Standard data table',
  },
  {
    value: 'view',
    label: 'View',
    description: 'Virtual table defined by query',
  },
  {
    value: 'external',
    label: 'External',
    description: 'Data stored outside the catalog',
  },
];
